import { Box, Typography, BoxProps } from '@mui/material';
import React, { ReactNode } from 'react';

interface SectionTitleProps {
  children: ReactNode;
  variant?: 'xs' | 'sm' | 'md' | 'lg' | 'xsb';
  sx?: Record<string, any>;
}

export const SectionTitle: React.FC<SectionTitleProps> = ({ children, variant = 'md', sx }) => {
  const fontSizes = {
    xs: { xs: 14, md: 16 },
    xsb: { xs: 14, md: 18 },
    sm: { xs: 18, sm: 20, md: 24 },
    md: { xs: 22, sm: 28, md: 30 },
    lg: { xs: 28, sm: 36, md: 48 },
  };

  return (
    <Typography
      className="font-satoshi-bold"
      sx={{
        fontSize: fontSizes[variant],
        lineHeight: 1.1,
        mb: '12px',
        color: variant === 'xs' ? '#00000080' : '#000',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

interface SectionHeadingProps {
  subtitle?: ReactNode;
  title: ReactNode;
  marginTop?: boolean;
  isBold?: boolean;
}

export const SectionHeading: React.FC<SectionHeadingProps> = ({
  subtitle,
  title,
  marginTop = true,
  isBold = false,
}) => {
  return (
    <>
      {subtitle && <SectionTitle variant="xs">{subtitle}</SectionTitle>}
      <Typography
        className={isBold ? 'font-satoshi-bold' : 'font-satoshi-regular'}
        sx={{
          fontSize: { xs: 32, sm: 32, md: 40 },
          mt: subtitle ? '12px' : marginTop ? '0px' : '0px',
          lineHeight: '110%',
          pb: '12px',
        }}
      >
        {title}
      </Typography>
    </>
  );
};

interface SectionDescriptionProps {
  children: ReactNode;
  mt?: string | number | Record<string, any>;
  sx?: Record<string, any>;
  bold?: boolean;
}

export const SectionDescription: React.FC<SectionDescriptionProps> = ({
  children,
  mt = '12px',
  sx = {},
  bold = false,
}) => {
  return (
    <Typography
      className="font-satoshi-regular"
      sx={{
        fontSize: '18px',
        mt: mt,
        lineHeight: '150%',
        fontWeight: bold ? 'bold' : 'normal',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

export const SectionMediumDescription: React.FC<SectionDescriptionProps> = ({
  children,
  mt = '12px',
  sx = {},
  bold = false,
}) => {
  return (
    <Typography
      className={bold ? 'font-satoshi-bold' : 'font-satoshi-medium'}
      sx={{
        fontSize: '18px',
        mt: mt,
        lineHeight: '150%',
        fontWeight: bold ? 'bold' : 'normal',
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
};

interface BulletListProps {
  items: ReactNode[];
  mt?: string | number | Record<string, any>;
  pl?: string;
  type?: 'disc' | 'circle' | 'square' | 'none';
}

export const BulletList: React.FC<BulletListProps> = ({
  items,
  mt = { xs: '20px', md: '31px' },
  pl = '20px',
  type = 'disc',
}) => {
  return (
    <Box sx={{ mt, ml: { xs: '0', md: '5px' } }}>
      <ul
        style={{
          listStyleType: type,
          paddingLeft: pl,
          margin: 0,
        }}
      >
        {items.map((item, index) => (
          <li key={index}>
            <Typography
              className="font-satoshi-regular"
              sx={{
                fontSize: { xs: 16, md: 18 },
                lineHeight: '160%',
                mb: '12px',
              }}
            >
              {item}
            </Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
};

interface NumberedListProps {
  items: ReactNode[];
  mt?: string | number | Record<string, any>;
}

export const NumberedList: React.FC<NumberedListProps> = ({
  items,
  mt = { xs: '20px', md: '31px' },
}) => {
  return (
    <Box sx={{ mt, ml: { xs: '0', md: '5px' } }}>
      <ul
        style={{
          listStyleType: 'decimal',
          paddingLeft: '40px',
          marginTop: '12px',
        }}
      >
        {items.map((item, index) => (
          <li key={index}>
            <Typography
              className="font-satoshi-regular"
              sx={{
                fontSize: { xs: 16, md: 17 },
                lineHeight: '160%',
                mb: '12px',
              }}
            >
              {item}
            </Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
};

interface ListWithHeaderProps {
  header: ReactNode;
  items: ReactNode[];
  isNumbered?: boolean;
}

export const ListWithHeader: React.FC<ListWithHeaderProps> = ({
  header,
  items,
  isNumbered = false,
}) => {
  return (
    <Box sx={{ mt: { xs: '30px', md: '31px' }, ml: { xs: '0', md: '5px' } }}>
      <Typography
        className="font-satoshi-bold"
        sx={{
          fontSize: { xs: 16, md: 17 },
          lineHeight: '160%',
          mt: '20px',
        }}
      >
        {header}
      </Typography>
      <ul
        style={{
          listStyleType: isNumbered ? 'decimal' : 'disc',
          paddingLeft: isNumbered ? '40px' : '40px',
          marginTop: '12px',
        }}
      >
        {items.map((item, index) => (
          <li key={index}>
            <Typography
              className="font-satoshi-regular"
              sx={{
                fontSize: { xs: 16, md: 17 },
                lineHeight: '160%',
                mb: '12px',
              }}
            >
              {item}
            </Typography>
          </li>
        ))}
      </ul>
    </Box>
  );
};

interface NestedListItem {
  content: ReactNode;
  subItems?: ReactNode[];
  isNumberedSub?: boolean;
}

interface NestedBulletListProps {
  items: NestedListItem[];
  mtMain?: string | number | Record<string, any>;
  type?: 'disc' | 'circle' | 'square';
}

export const NestedBulletList: React.FC<NestedBulletListProps> = ({
  items,
  mtMain = { xs: '20px', md: '31px' },
  type = 'disc',
}) => {
  return (
    <Box sx={{ mt: mtMain, ml: { xs: '0', md: '5px' } }}>
      <ul
        style={{
          listStyleType: type,
          paddingLeft: '20px',
          margin: 0,
        }}
      >
        {items.map((item, index) => (
          <li key={index}>
            <Typography
              className="font-satoshi-regular"
              sx={{
                fontSize: { xs: 16, md: 18 },
                lineHeight: '160%',
                mb: item.subItems ? '12px' : '12px',
              }}
            >
              {item.content}
            </Typography>
            {item.subItems && item.subItems.length > 0 && (
              <ul
                style={{
                  listStyleType: item.isNumberedSub ? 'decimal' : 'circle',
                  paddingLeft: '40px',
                  marginTop: '8px',
                  marginBottom: '12px',
                }}
              >
                {item.subItems.map((subItem, subIndex) => (
                  <li key={subIndex}>
                    <Typography
                      className="font-satoshi-regular"
                      sx={{
                        fontSize: { xs: 14, md: 16 },
                        lineHeight: '150%',
                        mb: '8px',
                      }}
                    >
                      {subItem}
                    </Typography>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </Box>
  );
};

interface NestedListWithHeaderProps {
  header: ReactNode;
  items: NestedListItem[];
  isNumbered?: boolean;
  type?: 'disc' | 'circle' | 'square';
}

export const NestedListWithHeader: React.FC<NestedListWithHeaderProps> = ({
  header,
  items,
  isNumbered = false,
  type = 'disc',
}) => {
  return (
    <Box sx={{ mt: { xs: '20px', md: '31px' }, ml: { xs: '0', md: '5px' } }}>
      <Typography
        className="font-satoshi-bold"
        sx={{
          fontSize: { xs: 16, md: 17 },
          lineHeight: '160%',
          mt: '20px',
        }}
      >
        {header}
      </Typography>
      <ul
        style={{
          listStyleType: isNumbered ? 'decimal' : type,
          paddingLeft: '40px',
          marginTop: '12px',
        }}
      >
        {items.map((item, index) => (
          <li key={index}>
            <Typography
              className="font-satoshi-regular"
              sx={{
                fontSize: { xs: 16, md: 17 },
                lineHeight: '160%',
                mb: item.subItems ? '12px' : '12px',
              }}
            >
              {item.content}
            </Typography>
            {item.subItems && item.subItems.length > 0 && (
              <ul
                style={{
                  listStyleType: item.isNumberedSub ? 'decimal' : 'circle',
                  paddingLeft: '40px',
                  marginTop: '8px',
                  marginBottom: '12px',
                }}
              >
                {item.subItems.map((subItem, subIndex) => (
                  <li key={subIndex}>
                    <Typography
                      className="font-satoshi-regular"
                      sx={{
                        fontSize: { xs: 14, md: 16 },
                        lineHeight: '150%',
                        mb: '8px',
                      }}
                    >
                      {subItem}
                    </Typography>
                  </li>
                ))}
              </ul>
            )}
          </li>
        ))}
      </ul>
    </Box>
  );
};

interface FigureWithCaptionProps {
  src: string;
  alt: string;
  title?: string;
  caption: ReactNode;
  mt?: string | number | Record<string, any>;
}

export const FigureWithCaption: React.FC<FigureWithCaptionProps> = ({
  src,
  alt,
  title,
  caption,
  mt = '74px',
}) => {
  return (
    <Box
      component="figure"
      sx={{
        margin: 0,
        width: '100%',
        position: 'relative',
        mt: mt,
      }}
    >
      <img
        src={src}
        alt={alt}
        title={title}
        loading="lazy"
        style={{
          width: '100%',
          height: 'auto',
          display: 'block',
          borderRadius: '2px',
        }}
      />
      <Box
        component="figcaption"
        sx={{
          mt: 1,
          fontSize: 12,
          color: '#ffffff',
          fontStyle: 'italic',
          textAlign: 'center',
          lineHeight: '120%',
        }}
        className="font-satoshi-regular"
      >
        {caption}
      </Box>
    </Box>
  );
};

interface SectionBoxProps {
  children: ReactNode;
  mt?: string | number | Record<string, any>;
  id?: string;
}

export const SectionBox: React.FC<SectionBoxProps & BoxProps> = ({
  children,
  mt = { xs: '50px', md: '100px' },
  id,
  ...props
}) => {
  return (
    <Box sx={{ mt, ...props?.sx }} id={id} {...props}>
      {children}
    </Box>
  );
};

interface LargeHeadingProps {
  children: ReactNode;
  mt?: string | number | Record<string, any>;
  mb?: string | number | Record<string, any>;
}

export const LargeHeading: React.FC<LargeHeadingProps> = ({
  children,
  mt = { xs: '50px', md: '100px' },
  mb = 0,
}) => {
  return (
    <Typography
      className="font-satoshi-regular"
      sx={{
        fontSize: { xs: 32, sm: 40, md: 48 },
        lineHeight: '110%',
        mb: mb,
        color: '#000',
        mt: mt,
      }}
    >
      {children}
    </Typography>
  );
};
